/**
 * Workflow Chaining List View
 * 
 * Main view with collapsible diagram, tabs, and subscription management table
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Collapse,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import AddIcon from '@mui/icons-material/Add';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { ViewToolbar } from '@/components/ViewToolbar';
import { WorkflowChainingDiagram } from './WorkflowChainingDiagram';
import { EventCatalogView } from './EventCatalogView';
import { useWorkflowSubscriptions, useWorkflowDefinitions, useCommandCatalog } from './hooks/useWorkflowChaining';
import { workflowDefinitionsApi } from './api';
import type { WorkflowDefinitionSummary } from './types';

/**
 * Workflows Tab Component
 */
const WorkflowsTab: React.FC = () => {
  const { data: workflowsData, isLoading, refetch } = useWorkflowDefinitions();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [workflowToDelete, setWorkflowToDelete] = React.useState<WorkflowDefinitionSummary | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  // Handle both array response and wrapped response (e.g., { items: [...] })
  const workflows = Array.isArray(workflowsData)
    ? workflowsData
    : (workflowsData as any)?.items ?? [];

  const handleDeleteClick = (workflow: WorkflowDefinitionSummary) => {
    setWorkflowToDelete(workflow);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!workflowToDelete) return;

    setIsDeleting(true);
    setActionError(null);
    try {
      await workflowDefinitionsApi.deleteWorkflowVersion(workflowToDelete.id);
      await refetch();
    } catch (error: any) {
      setActionError(error.message || 'Failed to delete workflow');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  if (isLoading) {
    return <Typography>Loading workflows...</Typography>;
  }

  return (
    <Box>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 1 }}>
          {actionError}
        </Alert>
      )}
      <Typography variant="h6" gutterBottom>
        Elsa Workflows
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Old unpublished versions can be safely deleted. Published and latest versions cannot be deleted.
      </Typography>
      <DataGridPremium
        density="compact"
        rowHeight={GRID_ROW_HEIGHT}
        rows={workflows || []}
        getRowId={(row) => row.id}
        columns={[
          {
            field: '_rowActions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            getActions: (params) => [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                disabled={params.row.isPublished || params.row.isLatest}
                onClick={() => handleDeleteClick(params.row)}
              />,
            ],
          },
          { field: 'definitionId', headerName: 'Definition ID', flex: 1 },
          { field: 'name', headerName: 'Name', flex: 1 },
          { field: 'version', headerName: 'Version', width: 100 },
          { field: 'isPublished', headerName: 'Published', width: 100, type: 'boolean' },
          { field: 'isLatest', headerName: 'Latest', width: 100, type: 'boolean' },
        ]}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Workflow Version</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{workflowToDelete?.name} v{workflowToDelete?.version}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. Only old, unused versions should be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Commands Tab Component
 */
const CommandsTab: React.FC = () => {
  const { data: commands, isLoading } = useCommandCatalog();

  if (isLoading) {
    return <Typography>Loading commands...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Commands
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Commands that can trigger events and workflows. This information is loaded from CLI-generated metadata.
      </Typography>
      <DataGridPremium
        density="compact"
        rowHeight={GRID_ROW_HEIGHT}
        rows={commands || []}
        getRowId={(row) => row.fullName}
        columns={[
          { field: 'name', headerName: 'Command', flex: 1 },
          { field: 'module', headerName: 'Module', width: 120 },
          { field: 'entity', headerName: 'Entity', width: 120 },
          { field: 'description', headerName: 'Description', flex: 2 },
          { field: 'hasValidation', headerName: 'Validation', width: 100, type: 'boolean' },
          { field: 'hasBusinessRules', headerName: 'Rules', width: 90, type: 'boolean' },
          { field: 'hasRuleSets', headerName: 'RuleSets', width: 100, type: 'boolean' },
          { 
            field: 'events', 
            headerName: 'Events Emitted', 
            flex: 1,
            renderCell: (params) => params.row.events?.join(', ') || '-'
          },
        ]}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
      />
    </Box>
  );
};

export const WorkflowChainingListView: React.FC = () => {
  const navigate = useNavigate();
  const [diagramExpanded, setDiagramExpanded] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const { data: subscriptionsData, isLoading } = useWorkflowSubscriptions({
    skipCount: paginationModel.page * paginationModel.pageSize,
    maxResultCount: paginationModel.pageSize,
  });

  const columns: GridColDef[] = [
    {
      field: '_rowActions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => navigate(`/core/workflow-chaining/subscriptions/${params.row.id}`)}
        />,
      ],
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => params.row.name || params.row.eventType,
    },
    {
      field: 'eventType',
      headerName: 'Event Type',
      flex: 1,
    },
    {
      field: 'workflowDefinitionId',
      headerName: 'Workflow',
      flex: 1,
    },
    {
      field: 'isEnabled',
      headerName: 'Enabled',
      width: 100,
      type: 'boolean',
    },
    {
      field: 'isDslDefined',
      headerName: 'DSL',
      width: 80,
      type: 'boolean',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Toolbar with actions on left */}
      <ViewToolbar>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/core/workflow-chaining/subscriptions/new')}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
          }}
        >
          New Subscription
        </Button>
      </ViewToolbar>

      {/* Page content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', px: 2, pb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          Workflow Chaining
        </Typography>

        {/* Collapsible Diagram */}
        <Paper sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              cursor: 'pointer',
            }}
            onClick={() => setDiagramExpanded(!diagramExpanded)}
          >
            <Typography variant="h6">Visual Diagram</Typography>
            <IconButton size="small">
              {diagramExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={diagramExpanded}>
            <Box sx={{ p: 2, pt: 0 }}>
              <WorkflowChainingDiagram />
            </Box>
          </Collapse>
        </Paper>

        {/* Tabs */}
        <Paper>
          <Tabs value={currentTab} onChange={(_, value) => setCurrentTab(value)}>
            <Tab label="Commands" />
            <Tab label="Events" />
            <Tab label="Subscriptions" />
            <Tab label="Workflows" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {currentTab === 0 && <CommandsTab />}
            {currentTab === 1 && <EventCatalogView />}
            {currentTab === 2 && (
              <DataGridPremium
                density="compact"
                rowHeight={GRID_ROW_HEIGHT}
                rows={subscriptionsData?.items || []}
                columns={columns}
                loading={isLoading}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                rowCount={subscriptionsData?.totalCount || 0}
                paginationMode="server"
                disableRowSelectionOnClick
                autoHeight
              />
            )}
            {currentTab === 3 && <WorkflowsTab />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

